<script setup lang="ts">
/**
 * Platform jobs — every Container Apps Job in this deployment, in one list.
 *
 * Data sources (ADR 0055) shows the same jobs under the source each one
 * feeds, which is where an admin usually starts. This is the other question:
 * "is anything on the platform not running?", answered without opening a
 * source. Late first, then failing — a late job emits nothing, so it is the
 * one silence hides (app/admin_jobs.py).
 *
 * Named "Platform jobs" so it is never confused with Scheduled jobs
 * (ADR 0071), which are assistant turns a person receives.
 */
import { computed, onMounted, ref } from "vue";
import { AdminError, type JobsResponse, getJobs } from "../api/admin";
import JobsTable from "./JobsTable.vue";
import SkeletonRows from "./SkeletonRows.vue";

const props = defineProps<{ devUser: string }>();

const data = ref<JobsResponse | null>(null);
const loading = ref(false);
const error = ref("");

async function reload(): Promise<void> {
  loading.value = true;
  error.value = "";
  try {
    data.value = await getJobs(props.devUser);
  } catch (e) {
    error.value = e instanceof AdminError ? e.message : String(e);
  } finally {
    loading.value = false;
  }
}
onMounted(reload);

function rank(j: JobsResponse["jobs"][number]): number {
  if (j.overdue) return 0;
  if (j.failure_open ?? j.last_status.toLowerCase() === "failed") return 1;
  return 2;
}

const jobs = computed(() =>
  [...(data.value?.jobs ?? [])].sort(
    (a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name),
  ),
);
</script>

<template>
  <div class="platform-jobs">
    <header class="platform-jobs__header">
      <h2>Platform jobs</h2>
      <button class="btn" :disabled="loading" @click="reload">Refresh</button>
    </header>
    <p class="muted small">
      Every scheduled Container Apps Job here — ingest, sync and housekeeping —
      and whether it ran when its schedule said. The same jobs appear under
      their source in Data sources. Assistant turns on a schedule are under
      Assistants → Scheduled jobs.
    </p>
    <p v-if="error" class="platform-jobs__error" role="alert">{{ error }}</p>
    <p v-else-if="data && !data.available" class="muted">
      Job status is not available here: {{ data.reason }}
    </p>
    <SkeletonRows v-if="loading && !data" :rows="6" />
    <JobsTable v-else-if="data && data.available" :jobs="jobs" />
  </div>
</template>

<style scoped>
.platform-jobs {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}
.platform-jobs__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
}
.platform-jobs__header h2 {
  margin: 0;
}
.platform-jobs__error {
  color: var(--color-error);
  margin: 0;
}
.muted {
  color: var(--color-muted);
}
.small {
  font-size: 0.85rem;
}
</style>
